import { Col, FieldFeedback, Row, TipBox } from '@orobix/orobix-fe-components'
import { Formik, FormikHandlers, FormikHelpers, FormikState } from 'formik'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useDriverStyle from '../../components/Driver/driver.styles'
import MaxImagesAlertModal from '../../components/MaxImagesAlertModal/MaxImagesAlertModal'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import PrecisionAlertModal from '../../components/PrecisionAlertModal/PrecisionAlertModal'
import NotificationSettigs from '../../components/Settings/NotificationsSettings/NotificationsSettings'
import { SettingsContext } from '../../components/Settings/SettingsContext'
import useUpdateSettings from '../../components/Settings/useUpdateSettings'
import TrainingName from '../../components/TrainingName/TrainingName'
import TrainingNotes from '../../components/TrainingNotes/TrainingNotes'
import { useStore } from '../../store/store'
import { ProductType, SelectOption as FormSelectOption } from '../../types'
import { BackboneSelectOption } from '../../types/backbone'
import { ObservationCompact } from '../../types/observation'
import getDriver from './Driver/Driver'
import { Resolution } from './ResolutionSelect/useResolutionData'
import StartTestSwitch from './StartTestSwitch'
import StartTrainingButton from './StartTrainingButton/StartTrainingButton'
import StartTrainingConfirmationModal from './StartTrainingConfirmationModal/StartTrainingConfirmationModal'
import TimeBox from './TimeBox/TimeBox'
import useStyles from './TrainingNewForm.styles'
import { formatEstimatedTrainingsDuration, ITrainingDuration } from './utils'
import WarningBox from './WarningBox/WarningBox'

export type SelectOption = FormSelectOption

type ObservationsMixin = {
  selectedObservations: ObservationCompact[]
}

type BackboneMixin = {
  backbone?: BackboneSelectOption[]
}

type ResolutionMixin = {
  resolution?: Resolution
}

type TrainingInfoMixin = {
  name?: string
  notes?: string
}

type StartTestMixin = {
  startTest: boolean
}

type FP16Mixin = {
  fp16?: boolean
}

export type TrainingNewFormValues = ObservationsMixin &
  BackboneMixin &
  ResolutionMixin &
  TrainingInfoMixin &
  StartTestMixin &
  FP16Mixin

interface TrainingNewFormProps<T extends TrainingNewFormValues> {
  children?: React.ReactNode
  productId: string
  productType: ProductType
  requiredImages: number
  initialValues: T
  renderRightColumn: (props: {
    values: T
    setFieldValue: FormikHelpers<T>['setFieldValue']
    setFieldTouched: FormikHelpers<T>['setFieldTouched']
    handleBlur: FormikHandlers['handleBlur']
    isSubmitting: FormikState<T>['isSubmitting']
    errors: FormikState<T>['errors']
    touched: FormikState<T>['touched']
  }) => React.ReactNode
  validate: (values: T) => void
  onSubmit: (values: T, setSubmitting: (isSubmitting: boolean) => void) => void
  estimatedTrainingsDuration: ITrainingDuration[]
  hasNonAnnotatedImagesInDataset?: boolean
}

const TrainingNewForm = <T extends TrainingNewFormValues>({
  children,
  productId,
  productType,
  requiredImages,
  initialValues,
  renderRightColumn,
  validate,
  onSubmit,
  estimatedTrainingsDuration,
  hasNonAnnotatedImagesInDataset = false,
}: TrainingNewFormProps<T>) => {
  const { product } = useStore()
  const classes = useStyles()
  const classesDriver = useDriverStyle()
  const [
    startTrainingConfirmationModal,
    setStartTrainingConfirmationModal,
  ] = useState(false)
  const [showPrecisionAlert, setShowPrecisionAlert] = useState(false)
  const [showMaxImagesAlert, setShowMaxImagesAlert] = useState(false)

  const { t } = useTranslation()

  const { settings } = useContext(SettingsContext)
  const { updateSettings, inProgress } = useUpdateSettings()

  const guidedTourShown = useRef(false)

  const updatePreference = () => {
    const checkbox = document.getElementById(
      'doNotShowAgainCheckbox'
    ) as HTMLInputElement
    if (checkbox.checked === settings.preference__show_guided_tour) {
      updateSettings({
        preference__show_guided_tour: !checkbox.checked,
      })
    }
  }

  const driverObj = getDriver(
    classesDriver,
    settings,
    updateSettings,
    updatePreference,
    productType
  )

  useEffect(() => {
    if (guidedTourShown.current === false) {
      if (settings.preference__show_guided_tour && !driverObj.isActive()) {
        driverObj.drive()
        guidedTourShown.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.preference__show_guided_tour])

  return (
    <Formik<T>
      initialValues={initialValues}
      validate={(values) => validate(values)}
      onSubmit={(values, { setSubmitting }) => onSubmit(values, setSubmitting)}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        isValid,
        submitForm,
      }) => (
        // cannot use <form> tag here, see https://orobix.atlassian.net/browse/AIG-1101
        <div role="form" className={classes.form}>
          <Row noBottomPadding className={classes.row}>
            <Col xs={8} xl={9} className={classes.col}>
              {children || (
                <ObservationsSelectField
                  name="selectedObservations"
                  productType={productType}
                  productId={Number(productId)}
                  inTraining
                  inTest={false}
                />
              )}
            </Col>
            <Col xs={4} xl={3}>
              <div id="asideDivId">
                {renderRightColumn({
                  values,
                  setFieldValue,
                  setFieldTouched,
                  handleBlur,
                  isSubmitting,
                  errors,
                  touched,
                })}

                <TrainingName fieldName="name" />

                <TrainingNotes fieldName="notes" />

                {!!(
                  errors.selectedObservations &&
                  values.selectedObservations.length > 0
                ) && (
                  <div className={classes.selectedObservationsFeedback}>
                    <FieldFeedback>{errors.selectedObservations}</FieldFeedback>
                  </div>
                )}

                {hasNonAnnotatedImagesInDataset && (
                  <div className={classes.selectedObservationsFeedback}>
                    <FieldFeedback>
                      {t(
                        'One or more selected datasets contain non-annotated images, cannot start training.'
                      )}
                    </FieldFeedback>
                  </div>
                )}

                <StartTestSwitch fieldName="startTest" />

                <NotificationSettigs
                  isInSettingsPage={false}
                  isLoading={inProgress}
                  settings={settings}
                  updateSettings={updateSettings}
                />

                <div className={classes.row}>
                  <StartTrainingButton
                    isDisabled={
                      isSubmitting || !isValid || hasNonAnnotatedImagesInDataset
                    }
                    isLoading={isSubmitting}
                    onSkipConfirmation={() => {
                      if (
                        estimatedTrainingsDuration.find(
                          (d) =>
                            d.sec > 120 * 60 && d.backbone !== 'Logical Anomaly'
                        ) // 2 hours
                      ) {
                        setShowMaxImagesAlert(true)
                      } else if (
                        values.fp16 &&
                        settings?.preference__show_precision_alert_on_creation
                      ) {
                        setShowPrecisionAlert(true)
                      } else {
                        submitForm()
                      }
                    }}
                    onRequiresConfirmation={() =>
                      setStartTrainingConfirmationModal(true)
                    }
                  />
                </div>
                {product?.observationsNumber &&
                  values.selectedObservations.length >=
                    product?.observationsNumber && (
                    <div
                      className={classes.tipBox}
                      data-testid="divSelectAllWarningId"
                    >
                      <WarningBox>
                        {t(
                          'By selecting all the images, you will need to load new ones for testing.'
                        )}
                      </WarningBox>
                    </div>
                  )}
                <div className={classes.tipBox}>
                  <TimeBox>
                    {isValid
                      ? formatEstimatedTrainingsDuration(
                          estimatedTrainingsDuration
                        )
                      : t(
                          'Fill in all the required fields to see the estimated training duration.'
                        )}
                  </TimeBox>
                </div>

                <div className={classes.tipBox}>
                  <TipBox>
                    {t(
                      'To start a new training, select at least {{ count }} annotated images. Please note, all the selected images need to be annotated.',
                      { count: requiredImages }
                    )}
                  </TipBox>
                </div>
              </div>
            </Col>
          </Row>

          {startTrainingConfirmationModal && (
            <StartTrainingConfirmationModal
              onCloseRequest={() => setStartTrainingConfirmationModal(false)}
              onStartTrainingRequest={() => {
                if (
                  estimatedTrainingsDuration.find(
                    (d) => d.sec > 120 * 60 && d.backbone !== 'Logical Anomaly'
                  ) // 2 hours
                ) {
                  setShowMaxImagesAlert(true)
                } else if (
                  values.fp16 &&
                  settings?.preference__show_precision_alert_on_creation
                ) {
                  setShowPrecisionAlert(true)
                } else {
                  submitForm()
                }
              }}
            />
          )}
          {showMaxImagesAlert && (
            <MaxImagesAlertModal
              handleClose={() => {
                setShowMaxImagesAlert(false)
              }}
              handleConfirm={() => {
                if (values.fp16) {
                  setShowPrecisionAlert(true)
                } else {
                  submitForm()
                }
              }}
              isAnomaly={productType === 'anomaly'}
            />
          )}
          {showPrecisionAlert && (
            <PrecisionAlertModal
              action="creation"
              handleClose={() => {
                setShowPrecisionAlert(false)
                submitForm()
              }}
            />
          )}
        </div>
      )}
    </Formik>
  )
}

export default TrainingNewForm
